// Scrollytelling Sequence Animation
// Load GSAP and ScrollTrigger from CDN
function loadGSAP() {
  return new Promise((resolve) => {
    if (window.gsap && window.ScrollTrigger) {
      resolve();
      return;
    }

    const gsapScript = document.createElement("script");
    gsapScript.src =
      "https://cdn.jsdelivr.net/npm/gsap@3.12.5/dist/gsap.min.js";
    gsapScript.onload = () => {
      const scrollTriggerScript = document.createElement("script");
      scrollTriggerScript.src =
        "https://cdn.jsdelivr.net/npm/gsap@3.12.5/dist/ScrollTrigger.min.js";
      scrollTriggerScript.onload = () => {
        gsap.registerPlugin(ScrollTrigger);
        resolve();
      };
      document.head.appendChild(scrollTriggerScript);
    };
    document.head.appendChild(gsapScript);
  });
}

// "Bon appétit!" backdrop marquee. Driven by the Web Animations API, not a
// CSS animation: ScrollTrigger's pin re-parents the wrapper on every
// refresh(), and re-parenting resets CSS animations (the marquee visibly
// snapped back whenever anything queued a refresh). WAAPI animations belong
// to the element object, so they keep their clock through re-parenting.
function initFinaleMarquee() {
  const track = document.querySelector(".combined-finale__backdrop-scroll");
  if (!track || !track.animate) return;
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

  const SPEED = 90; // px per second

  let animation = null;
  const start = () => {
    // The track holds two identical copies; shifting by one copy's width
    // lands on an identical frame, making the infinite loop seamless.
    const copyWidth = track.scrollWidth / 2;
    if (!copyWidth) return;

    // Carry loop progress over so re-measures don't visibly jump
    const progress = animation
      ? (animation.currentTime / animation.effect.getTiming().duration) % 1
      : 0;
    animation?.cancel();

    const duration = (copyWidth / SPEED) * 1000;
    animation = track.animate(
      [
        { transform: "translateX(0)" },
        { transform: `translateX(-${copyWidth}px)` },
      ],
      { duration, iterations: Infinity }
    );
    animation.currentTime = progress * duration;
  };

  start();

  // Copy width changes when DM Sans swaps in and at font-size breakpoints
  if (document.fonts?.ready) document.fonts.ready.then(start);
  let resizeTimeout;
  window.addEventListener("resize", () => {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(start, 200);
  });
}

// Initialize scrollytelling
async function initScrollytelling() {
  initFinaleMarquee();

  await loadGSAP();

  const wrapper = document.querySelector(".scrollytelling-wrapper");
  const scenes = document.querySelectorAll(".scene");

  if (!wrapper || scenes.length === 0) return;

  // The theme sets `scroll-behavior: smooth`, which turns ScrollTrigger's
  // internal scroll restoration during refresh() into an animation. Positions
  // then get measured mid-scroll and the pin start/end come out wrong.
  // Force instant scrolling for the duration of every refresh.
  ScrollTrigger.addEventListener("refreshInit", () => {
    document.documentElement.style.scrollBehavior = "auto";
  });
  ScrollTrigger.addEventListener("refresh", () => {
    document.documentElement.style.scrollBehavior = "";
  });

  // Create master timeline. The creation-time measurement needs the same
  // instant-scroll guard as refreshes (see listeners above).
  document.documentElement.style.scrollBehavior = "auto";
  const masterTimeline = gsap.timeline({
    scrollTrigger: {
      trigger: wrapper,
      pin: true,
      start: "top top",
      end: "+=10900",
      scrub: 1,
    },
  });
  document.documentElement.style.scrollBehavior = "";

  // Scene durations - Scene 1 held a bit longer so bubbles finish before fade; Scene 2 is 5 seconds; Scene 4 is 3.5s for finale animation
  const sceneDurations = [1.4, 5, 1, 3.5];
  const fadeOverlap = 0.2;

  scenes.forEach((scene, index) => {
    // Calculate cumulative start time
    const sceneStart = sceneDurations
      .slice(0, index)
      .reduce((sum, duration) => sum + duration, 0);
    const sceneDuration = sceneDurations[index];

    if (index === 0) {
      // First scene: traditional fade in, hold, fade out
      // Fade in
      masterTimeline.fromTo(
        scene,
        { opacity: 0 },
        { opacity: 1, duration: fadeOverlap },
        sceneStart
      );

      // Hold at full opacity
      masterTimeline.to(
        scene,
        { opacity: 1, duration: sceneDuration - fadeOverlap * 2 },
        sceneStart + fadeOverlap
      );
    } else {
      // Subsequent scenes: fade in overlaps with previous scene's fade out
      // Fade in (starts earlier to overlap)
      masterTimeline.fromTo(
        scene,
        { opacity: 0 },
        { opacity: 1, duration: fadeOverlap },
        sceneStart - fadeOverlap
      );

      // Hold at full opacity (longer duration since fade in started earlier)
      masterTimeline.to(
        scene,
        { opacity: 1, duration: sceneDuration - fadeOverlap },
        sceneStart
      );
    }

    // Fade out (overlaps with next scene's fade in)
    if (index < scenes.length - 1) {
      masterTimeline.to(
        scene,
        { opacity: 0, duration: fadeOverlap },
        sceneStart + sceneDuration - fadeOverlap
      );
    }

    // Scene-specific animations
    if (index === 0) {
      // Scene 1: Chat bubbles stagger
      const bubbles = scene.querySelectorAll(".chat-bubble");
      masterTimeline.to(
        bubbles,
        { opacity: 1, y: 0, duration: 0.3, stagger: 0.1 },
        sceneStart + 0.1
      );
    } else if (index === 3) {
      // Scene 4: Combined finale - Plate + Instagram + Hashtag animation sequence (3.5 seconds)
      const plate = scene.querySelector(".plate-overlay__plate");
      const chatBubble1 = scene.querySelector(
        '.chat-bubble--finale[data-bubble="1"]'
      );
      const chatBubble2 = scene.querySelector(
        '.chat-bubble--finale[data-bubble="2"]'
      );
      const socialPost = scene.querySelector(".social-post");
      const hashtag = scene.querySelector(".combined-finale__hashtag");
      const backdrop = scene.querySelector(".combined-finale__backdrop");
      const content = scene.querySelector(".combined-finale__content");

      // Set initial state for plate - off-screen to the right
      gsap.set(plate, {
        x: 200,
        rotation: 15,
      });

      // PHASE 1: Plate rolls in from right (0.0-0.3s)
      masterTimeline.to(
        plate,
        {
          opacity: 1,
          x: 0,
          rotation: 0,
          duration: 0.3,
          ease: "power2.out",
        },
        sceneStart + 0.0
      );

      // PHASE 2: Chat bubble 1 fades in and moves up (0.3-0.5s)
      masterTimeline.to(
        chatBubble1,
        {
          opacity: 1,
          y: 0,
          duration: 0.2,
          ease: "power2.out",
        },
        sceneStart + 0.3
      );

      // PHASE 3: Chat bubble 2 fades in and moves up (0.5-0.7s)
      masterTimeline.to(
        chatBubble2,
        {
          opacity: 1,
          y: 0,
          duration: 0.2,
          ease: "power2.out",
        },
        sceneStart + 0.5
      );

      // PHASE 4: Instagram post fades in behind plate (0.7-1.0s)
      masterTimeline.to(
        socialPost,
        {
          opacity: 1,
          duration: 0.3,
          ease: "power2.out",
        },
        sceneStart + 0.8
      );

      // PHASE 5: Hashtag fades in (1.0-1.2s) - positioned under plate, not visible yet
      masterTimeline.to(
        hashtag,
        {
          opacity: 1,
          duration: 0.2,
          ease: "power2.out",
        },
        sceneStart + 1.0
      );

      // PHASE 6: Plate + post + hashtag scale down together (1.2-1.8s)
      // This reveals the hashtag that was hidden under the plate
      masterTimeline.to(
        content,
        {
          scale: 0.85,
          duration: 0.5,
          ease: "power2.inOut",
        },
        sceneStart + 1.5
      );

      // PHASE 7: "Bon appétit!" backdrop fades in (1.8-2.2s)
      masterTimeline.to(
        backdrop,
        {
          opacity: 1,
          duration: 0.6,
          ease: "power2.out",
        },
        sceneStart + 1.6
      );
    } else if (index === 1) {
      // Scene 2: Complex multi-step sequence (5 seconds)
      const plates = scene.querySelectorAll(".plate-item");
      const labels = scene.querySelectorAll(".plate-item__label");
      const ingredients = scene.querySelectorAll(".ingredient-item");
      const shoppingBag = scene.querySelector(".shopping-bag");
      const textState1 = scene.querySelector(".scene-2-text--state-1");
      const textState2 = scene.querySelector(".scene-2-text--state-2");
      const textState3 = scene.querySelector(".scene-2-text--state-3");
      const letsCookSvgContainer = scene.querySelector(".lets-cook-svg");
      const letsCookSvg = scene.querySelector(".lets-cook-svg__image");
      const container = scene.querySelector(".scene-2-container");

      // Get container dimensions for calculations
      const containerWidth = container.offsetWidth;
      const containerHeight = container.offsetHeight;

      // Helper function to convert percentage position to pixel transform from center
      const getTransform = (leftPercent, topPercent) => {
        // Container center is at 50%, 50%
        // Calculate offset from center in pixels
        const xOffset = ((leftPercent - 50) / 100) * containerWidth;
        const yOffset = ((topPercent - 50) / 100) * containerHeight;
        return { x: xOffset, y: yOffset };
      };

      // Define scattered positions (random-looking arrangement)
      const scatteredPositions = [
        { left: 30, top: 35, rotation: -12, scale: 0.9 }, // mon
        { left: 65, top: 28, rotation: 8, scale: 1.05 }, // tue
        { left: 45, top: 55, rotation: -5, scale: 0.95 }, // wed
        { left: 70, top: 62, rotation: 15, scale: 1.0 }, // thu
        { left: 25, top: 68, rotation: -18, scale: 0.92 }, // fri
      ];

      // Define organized positions (horizontal row at 50% top, spread across width)
      const organizedPositions = [
        { left: 20, top: 50, rotation: 0, scale: 1 }, // mon
        { left: 35, top: 50, rotation: 0, scale: 1 }, // tue
        { left: 50, top: 50, rotation: 0, scale: 1 }, // wed
        { left: 65, top: 50, rotation: 0, scale: 1 }, // thu
        { left: 80, top: 50, rotation: 0, scale: 1 }, // fri
      ];

      // PHASE 0: Set z-index, centering, and scattered positions immediately (before any animations)
      // Monday on top, Friday on bottom
      plates.forEach((plate, plateIndex) => {
        const pos = scatteredPositions[plateIndex];
        const transform = getTransform(pos.left, pos.top);
        gsap.set(plate, {
          zIndex: plates.length - plateIndex,
          xPercent: -50,
          yPercent: -50,
          x: transform.x,
          y: transform.y,
          rotation: pos.rotation,
          scale: pos.scale,
        });
      });
      labels.forEach((label, labelIndex) => {
        gsap.set(label, { zIndex: labels.length - labelIndex });
      });

      // Set initial SVG dimensions
      gsap.set(letsCookSvg, {
        attr: { width: "70vw", height: "26vw" },
      });

      // PHASE 1: Fade in scattered (0.1-0.3s)
      masterTimeline.to(
        plates,
        { opacity: 1, duration: 0.2, stagger: 0.05 },
        sceneStart + 0.1
      );

      // PHASE 2: Hold scattered (0.3-0.6s) - no animation, plates visible in scattered state

      // PHASE 3: Scattered → Organized (0.6-1.0s)
      plates.forEach((plate, plateIndex) => {
        const pos = organizedPositions[plateIndex];
        const transform = getTransform(pos.left, pos.top);
        masterTimeline.to(
          plate,
          {
            xPercent: -50,
            yPercent: -50,
            x: transform.x,
            y: transform.y,
            rotation: pos.rotation,
            scale: pos.scale,
            duration: 0.4,
            ease: "power2.out",
          },
          sceneStart + 0.6
        );
      });

      // Fade in day labels
      masterTimeline.to(
        labels,
        { opacity: 1, xPercent: -50, x: 0, y: 0, duration: 0.2, stagger: 0.05 },
        sceneStart + 0.7
      );

      // PHASE 4: Organized → Stacked (1.0-1.4s)
      // All plates move to center (z-index already set in Phase 0)
      plates.forEach((plate, plateIndex) => {
        const xOffset = plateIndex * 16; // Stack with 16px offset
        masterTimeline.to(
          plate,
          {
            xPercent: -50,
            yPercent: -50,
            x: xOffset,
            y: 0,
            rotation: 0,
            scale: 1,
            duration: 0.4,
            ease: "power2.inOut",
          },
          sceneStart + 1.0
        );
      });

      // Stack labels with same offset
      labels.forEach((label, labelIndex) => {
        masterTimeline.to(
          label,
          {
            xPercent: -50, // Maintain centering
            x: labelIndex * 8, // Add horizontal offset
            y: -40, // Position above plate
            duration: 0.4,
            ease: "power2.inOut",
          },
          sceneStart + 1.0
        );
      });

      // PHASE 5: 4 plates fade out (1.4-1.6s)
      masterTimeline.to(
        [plates[1], plates[2], plates[3], plates[4]],
        { opacity: 0, duration: 0.2 },
        sceneStart + 1.4
      );

      masterTimeline.to(
        [labels[0], labels[1], labels[2], labels[3], labels[4]],
        { opacity: 0, duration: 0.2 },
        sceneStart + 1.4
      );

      // PHASE 6: Center plate moves (1.6-1.7s)
      masterTimeline.to(
        plates[0],
        {
          xPercent: -50,
          yPercent: -50,
          y: 0,
          scale: 2,
          duration: 0.1,
          ease: "power2.out",
        },
        sceneStart + 1.6
      );

      // PHASE 7: Text change to state 2 (1.7s)
      masterTimeline.to(
        textState1,
        { opacity: 0, duration: 0.2 },
        sceneStart + 1.7
      );
      masterTimeline.to(
        textState2,
        { opacity: 1, duration: 0.2 },
        sceneStart + 1.7
      );

      // PHASE 8: Ingredients radiate out (1.7-2.2s)
      const radius = 200;
      const angles = [0, 60, 120, 180, 240, 300];

      ingredients.forEach((ingredient, ingIndex) => {
        const angle = (angles[ingIndex] * Math.PI) / 180;
        const xPos = Math.cos(angle) * radius;
        const yPos = Math.sin(angle) * radius;

        masterTimeline.to(
          ingredient,
          {
            opacity: 1,
            xPercent: -50,
            yPercent: -50,
            x: xPos,
            y: yPos,
            duration: 0.5,
            ease: "power2.out",
          },
          sceneStart + 1.7 + ingIndex * 0.05
        );
      });

      // PHASE 9: PAUSE/HOLD (2.2-2.9s) - no animations

      // PHASE 10: Text change to state 3 (2.9s)
      masterTimeline.to(
        textState2,
        { opacity: 0, duration: 0.2 },
        sceneStart + 2.9
      );
      masterTimeline.to(
        textState3,
        { opacity: 1, duration: 0.2 },
        sceneStart + 2.9
      );

      // PHASE 11: Ingredients slide back to center (2.9-3.2s) and plate disappears
      masterTimeline.to(
        plates[0],
        { opacity: 0, duration: 0.2 },
        sceneStart + 2.9
      );

      // Define manual positions for each ingredient (peeking out of bag)
      const ingredientFinalPositions = [
        { x: -60, y: -60 }, // ingredient 1 - left side
        { x: -20, y: -70 }, // ingredient 2
        { x: 20, y: -75 }, // ingredient 3 meat
        { x: 60, y: -80 }, // ingredient 4 - right side
        { x: -40, y: -70 }, // ingredient 5
        { x: 50, y: -45 }, // ingredient 6
      ];

      ingredients.forEach((ingredient, ingIndex) => {
        const finalPos = ingredientFinalPositions[ingIndex];
        masterTimeline.to(
          ingredient,
          {
            xPercent: -50,
            yPercent: -50,
            scale: 2,
            x: finalPos.x,
            y: finalPos.y,
            duration: 0.4,
            ease: "power2.in",
          },
          sceneStart + 2.9 + ingIndex * 0.02
        );
      });

      // PHASE 12: Shopping bag slides up (3.2-3.6s)
      masterTimeline.to(
        shoppingBag,
        {
          opacity: 1,
          top: "50%",
          xPercent: -50,
          yPercent: -50,
          y: 0,
          duration: 0.4,
          ease: "power2.out",
        },
        sceneStart + 3.0
      );
      // Bag and ingredients fade out
      masterTimeline.to(
        [shoppingBag, ...ingredients],
        { opacity: 0, duration: 0.2 },
        sceneStart + 3.6
      );

      // PHASE 13: HOLD with shopping bag (3.6s) - brief pause

      // PHASE 14: "Let's Cook!" SVG fades in (3.6-3.9s)
      masterTimeline.to(
        letsCookSvgContainer,
        {
          opacity: 1,
          duration: 0.3,
          ease: "power2.out",
        },
        sceneStart + 3.6
      );

      // PHASE 15: "Let's Cook!" SVG scales up massively - one letter fills viewport (3.9-5.0s)
      masterTimeline.to(
        letsCookSvg,
        {
          attr: { width: "6410vw", height: "2390vw" }, // Scale by animating SVG dimensions directly
          duration: 1.1,
          ease: "power2.inOut",
        },
        sceneStart + 3.9
      );
    }
  });

  // Pin positions are measured at init, which can happen before images above
  // this section finish loading, or mid-flight during an animated scroll
  // restoration after reload (scroll-behavior: smooth makes Chrome animate
  // it). Stale measurements make the pin release at the wrong scroll
  // position, causing a blank gap or a visible jump at the hand-off.
  // Re-measure whenever the page's flow height changes, and self-heal by
  // comparing the recorded pin start against the real DOM position whenever
  // scrolling settles.
  let refreshTimeout;
  const queueRefresh = () => {
    clearTimeout(refreshTimeout);
    refreshTimeout = setTimeout(() => ScrollTrigger.refresh(), 200);
  };
  if (document.readyState !== "complete") {
    window.addEventListener("load", queueRefresh);
  }
  new ResizeObserver(queueRefresh).observe(document.body);

  const pinTrigger = masterTimeline.scrollTrigger;
  const pinSpacer = wrapper.parentElement;
  const syncIfDrifted = () => {
    const trueStart = pinSpacer.getBoundingClientRect().top + window.scrollY;
    if (Math.abs(trueStart - pinTrigger.start) > 2) queueRefresh();
  };
  let lastDriftCheck = 0;
  let scrollSettleTimeout;
  window.addEventListener(
    "scroll",
    () => {
      const now = performance.now();
      if (now - lastDriftCheck > 600) {
        lastDriftCheck = now;
        syncIfDrifted();
      }
      clearTimeout(scrollSettleTimeout);
      scrollSettleTimeout = setTimeout(syncIfDrifted, 250);
    },
    { passive: true }
  );
}

// Initialize on DOM ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initScrollytelling);
} else {
  initScrollytelling();
}
